/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// STYLE ENGINE — reads stylesConfig.json, creates every regulatory style
// ═══════════════════════════════════════════════════════════════════════════
//
// Exports:
//   createAllStyles()             — full run: all custom + built-in styles
//   ensureRunStyles(ctx, runNum)  — per-run: ensures styles exist and are
//                                   correct for that run; repairs on the fly
//                                   if properties deviate from config.
//
// Usage in taskpane.ts:
//   import { createAllStyles, ensureRunStyles } from './styleEngine';
//   // Inside Office.onReady → await createAllStyles();
//   // Inside each run     → await ensureRunStyles(context, 7);
// ═══════════════════════════════════════════════════════════════════════════

import stylesConfig from "./stylesConfig.json";

// ─── Types for the JSON schema ───────────────────────────────────────────

interface FontDef {
  name?: string;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

interface ParagraphDef {
  spaceBefore?: number;
  spaceAfter?: number;
  leftIndent?: number;
  firstLineIndent?: number;
  alignment?: string;       // "Centered" | "Left" | "Right" | "Justified"
  keepWithNext?: boolean;
  keepTogether?: boolean;
  widowControl?: boolean;
}

interface ParagraphStyleDef {
  name: string;
  basedOn: string | null;
  font: FontDef;
  paragraph: ParagraphDef;
  quickStyle?: boolean;
  _baseFallback?: string;   // fallback if basedOn doesn't exist
  _ooxmlOnly?: string[];    // props needing template/OOXML (docs only)
}

interface CharacterStyleDef {
  name: string;
  basedOn: string | null;
  font: FontDef;
}

interface BuiltInStyleDef {
  name: string;
  font: FontDef;
  paragraph?: ParagraphDef;
  quickStyle?: boolean;
}

interface StylesConfig {
  paragraphStyles: ParagraphStyleDef[];
  characterStyles: CharacterStyleDef[];
  builtInStyles: BuiltInStyleDef[];
  runStyleMap: Record<string, string[]>;
}

const config = stylesConfig as unknown as StylesConfig;

// ─── Result tracker ──────────────────────────────────────────────────────

export interface StyleCreationResult {
  total: number;
  created: number;
  existing: number;
  errors: string[];
}

// ─── Alignment string → enum mapper ──────────────────────────────────────

function toAlignment(val?: string): Word.Alignment | null {
  if (!val) return null;
  const map: Record<string, Word.Alignment> = {
    "centered":  Word.Alignment.centered,
    "left":      Word.Alignment.left,
    "right":     Word.Alignment.right,
    "justified": Word.Alignment.justified,
  };
  return map[val.toLowerCase()] ?? null;
}

// ─── Apply font properties from JSON def ─────────────────────────────────

function applyFont(target: Word.Font, def: FontDef): void {
  if (def.name    !== undefined) target.name   = def.name;
  if (def.size    !== undefined) target.size   = def.size;
  if (def.bold    !== undefined) target.bold   = def.bold;
  if (def.italic  !== undefined) target.italic = def.italic;
  if (def.color !== undefined && def.color !== "" && def.color !== "auto") target.color = def.color;
}

// ─── Apply paragraph properties from JSON def ────────────────────────────

function applyParagraph(
  target: Word.ParagraphFormat,
  def: ParagraphDef,
  resetIndent = false
): void {
  if (def.spaceBefore     !== undefined) target.spaceBefore     = def.spaceBefore;
  if (def.spaceAfter      !== undefined) target.spaceAfter      = def.spaceAfter;
  if (def.keepWithNext    !== undefined) target.keepWithNext     = def.keepWithNext;
  if (def.keepTogether    !== undefined) target.keepTogether     = def.keepTogether;
  if (def.widowControl    !== undefined) target.widowControl     = def.widowControl;

  // Indent: set from config if specified
  if (def.leftIndent      !== undefined) target.leftIndent      = def.leftIndent;
  if (def.firstLineIndent !== undefined) target.firstLineIndent = def.firstLineIndent;

  // When resetIndent is true and config has NO indent values,
  // explicitly zero them out to clear stale values from previously
  // misconfigured styles (e.g. List Alpha that had leftIndent from
  // an old version). Only used for styles with basedOn=null so
  // derived styles (Caption Figure → caption) keep their inheritance.
  if (resetIndent) {
    if (def.leftIndent      === undefined) target.leftIndent      = 0;
    if (def.firstLineIndent === undefined) target.firstLineIndent = 0;
  }

  const align = toAlignment(def.alignment);
  if (align !== null) target.alignment = align;
}

// ─── Ensure a style exists, create if missing ────────────────────────────

async function ensureStyle(
  context: Word.RequestContext,
  name: string,
  type: Word.StyleType
): Promise<{ style: Word.Style; wasCreated: boolean }> {
  const existing = context.document.getStyles().getByNameOrNullObject(name);
  existing.load("isNullObject");
  await context.sync();

  if (!existing.isNullObject) {
    return { style: existing, wasCreated: false };
  }

  const created = context.document.addStyle(name, type);
  await context.sync();
  return { style: created, wasCreated: true };
}

// ─── Try setting base style with fallback ────────────────────────────────

async function trySetBaseStyle(
  context: Word.RequestContext,
  style: Word.Style,
  baseName: string | null,
  fallback?: string
): Promise<void> {
  if (!baseName) return;
  try {
    style.baseStyle = baseName;
    await context.sync();
  } catch {
    if (fallback) {
      try {
        style.baseStyle = fallback;
        await context.sync();
      } catch {
        // Both failed — style stays un-based (inherits Normal)
      }
    }
  }
}


// ═══════════════════════════════════════════════════════════════════════════
//  createAllStyles()  — main export
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Creates or verifies every style defined in stylesConfig.json.
 * Safe to call multiple times — existing styles are updated,
 * missing styles are created.
 */
export async function createAllStyles(): Promise<StyleCreationResult> {
  const result: StyleCreationResult = { total: 0, created: 0, existing: 0, errors: [] };

  await Word.run(async (context) => {
    try {
      console.log("═".repeat(60));
      console.log("STYLE ENGINE: Creating all regulatory styles from config");
      console.log("═".repeat(60));

      // ── 1. Paragraph styles (order matters: base styles first) ──
      console.log("\n▸ Paragraph styles...");
      for (const def of config.paragraphStyles) {
        result.total++;
        try {
          const { style, wasCreated } = await ensureStyle(
            context, def.name, Word.StyleType.paragraph
          );

          if (wasCreated) {
            result.created++;
            console.log(`  ✓ Created: "${def.name}"`);
          } else {
            result.existing++;
          }

          // Always enforce the correct base style from config.
          // This repairs styles that were previously misconfigured
          // (e.g. List Alpha wrongly based on List Number → arabic 1,2,3).
          if (def.basedOn) {
            await trySetBaseStyle(context, style, def.basedOn, def._baseFallback);
          } else if (!wasCreated) {
            // Style exists but config says basedOn=null → reset to Normal
            // to clear any wrong inheritance (e.g. alpha inheriting List Number)
            try {
              style.baseStyle = "Normal";
              await context.sync();
            } catch { /* ignore — some styles can't change base */ }
          }

          applyFont(style.font, def.font);
          // resetIndent=true for styles with no base (basedOn=null):
          // clears stale leftIndent/firstLineIndent from previous versions.
          // Derived styles (basedOn set) skip reset to keep inheritance.
          applyParagraph(style.paragraphFormat, def.paragraph, !def.basedOn);
          if (def.quickStyle !== undefined) style.quickStyle = def.quickStyle;
          await context.sync();
        } catch (e) {
          console.warn(`  ⚠ "${def.name}": ${(e as Error).message}`);
          result.errors.push(def.name);
        }
      }

      // ── 2. Character styles ──
      console.log("▸ Character styles...");
      for (const def of config.characterStyles) {
        result.total++;
        try {
          const { style, wasCreated } = await ensureStyle(
            context, def.name, Word.StyleType.character
          );

          if (wasCreated) {
            result.created++;
            console.log(`  ✓ Created: "${def.name}"`);
            await trySetBaseStyle(context, style, def.basedOn);
          } else {
            result.existing++;
          }

          applyFont(style.font, def.font);
          await context.sync();
        } catch (e) {
          console.warn(`  ⚠ "${def.name}": ${(e as Error).message}`);
          result.errors.push(def.name);
        }
      }

      // ── 3. Built-in styles (update formatting, never create) ──
      console.log("▸ Built-in styles (Headings, Header, Footer, TOC)...");
      for (const def of config.builtInStyles) {
        result.total++;
        try {
          const s = context.document.getStyles().getByNameOrNullObject(def.name);
          s.load("isNullObject");
          await context.sync();

          if (!s.isNullObject) {
            applyFont(s.font, def.font);
            if (def.paragraph) applyParagraph(s.paragraphFormat, def.paragraph);
            if (def.quickStyle !== undefined) s.quickStyle = def.quickStyle;
            await context.sync();
            result.existing++;
          }
          // Built-in styles should always exist — skip creation
        } catch (e) {
          console.warn(`  ⚠ "${def.name}": ${(e as Error).message}`);
          result.errors.push(def.name);
        }
      }

      // ── Summary ──
      console.log("\n" + "─".repeat(60));
      console.log(`✅ Style creation complete`);
      console.log(`   Total checked:  ${result.total}`);
      console.log(`   Already exist:  ${result.existing}`);
      console.log(`   Newly created:  ${result.created}`);
      if (result.errors.length > 0) {
        console.log(`   ⚠ Errors (${result.errors.length}): ${result.errors.join(", ")}`);
      }
      console.log("─".repeat(60));

    } catch (error) {
      console.error("❌ Error in createAllStyles:", error);
      throw error;
    }
  });

  return result;
}


// ─── Diff helpers ────────────────────────────────────────────────────────

// Compares one live value against what config expects.
// Returns null if config has no opinion OR values already match.
// Returns a human-readable label string if they differ.
function diffValue<T>(label: string, live: T, expected: T | undefined): string | null {
  if (expected === undefined) return null;  // config doesn't specify this → leave it alone
  if (live === expected)      return null;  // already correct → nothing to do
  return `${label}: was "${live}" → set "${expected}"`;
}

// Compares every font property the config cares about against the live Word.Font.
// For each one that differs: patches the live font object AND records a fix label.
// Returns the list of fix labels (empty = font is already correct).
function diffAndRepairFont(live: Word.Font, def: FontDef): string[] {
  const fixes: string[] = [];

  const namefix = diffValue("font.name", live.name, def.name);
  if (namefix) { live.name = def.name!; fixes.push(namefix); }

  const sizefix = diffValue("font.size", live.size, def.size);
  if (sizefix) { live.size = def.size!; fixes.push(sizefix); }

  const boldfix = diffValue("font.bold", live.bold, def.bold);
  if (boldfix) { live.bold = def.bold!; fixes.push(boldfix); }

  const italicfix = diffValue("font.italic", live.italic, def.italic);
  if (italicfix) { live.italic = def.italic!; fixes.push(italicfix); }

  // Color: only repair when config gives a real value (not empty / "auto")
  if (def.color !== undefined && def.color !== "" && def.color !== "auto") {
    const colorfix = diffValue("font.color", live.color, def.color);
    if (colorfix) { live.color = def.color; fixes.push(colorfix); }
  }

  return fixes;
}

// Compares every paragraph property the config cares about against the live
// Word.ParagraphFormat. Same pattern: patches in place, returns fix labels.
function diffAndRepairParagraph(live: Word.ParagraphFormat, def: ParagraphDef): string[] {
  const fixes: string[] = [];

  const sba = diffValue("spaceBefore", live.spaceBefore, def.spaceBefore);
  if (sba) { live.spaceBefore = def.spaceBefore!; fixes.push(sba); }

  const saf = diffValue("spaceAfter", live.spaceAfter, def.spaceAfter);
  if (saf) { live.spaceAfter = def.spaceAfter!; fixes.push(saf); }

  const kwn = diffValue("keepWithNext", live.keepWithNext, def.keepWithNext);
  if (kwn) { live.keepWithNext = def.keepWithNext!; fixes.push(kwn); }

  const ktg = diffValue("keepTogether", live.keepTogether, def.keepTogether);
  if (ktg) { live.keepTogether = def.keepTogether!; fixes.push(ktg); }

  const wid = diffValue("widowControl", live.widowControl, def.widowControl);
  if (wid) { live.widowControl = def.widowControl!; fixes.push(wid); }

  const li = diffValue("leftIndent", live.leftIndent, def.leftIndent);
  if (li) { live.leftIndent = def.leftIndent!; fixes.push(li); }

  const fli = diffValue("firstLineIndent", live.firstLineIndent, def.firstLineIndent);
  if (fli) { live.firstLineIndent = def.firstLineIndent!; fixes.push(fli); }

  if (def.alignment !== undefined) {
    const expectedAlign = toAlignment(def.alignment);
    if (expectedAlign !== null && live.alignment !== expectedAlign) {
      live.alignment = expectedAlign;
      fixes.push(`alignment: was "${live.alignment}" → set "${def.alignment}"`);
    }
  }

  return fixes;
}


// ─── Repair existing styles for a run ────────────────────────────────────
//
// Called by ensureRunStyles for every style that already exists in the doc.
// Compares each property (font + paragraph + quickStyle) against the config
// definition and patches any that deviate. Mutations are queued on the proxy
// objects — caller does the context.sync() to commit them.
//
// Parameters:
//   existing   — style proxies that are confirmed present in the document
//                (isNullObject === false), each with name + loaded proxy
//   paraMap    — config defs for custom paragraph styles, keyed by name,
//                scoped to only this run's required styles
//   builtInMap — config defs for built-in styles (Headings etc.),
//                scoped to only this run's required styles
//   runNumber  — run number, used only for the console log label
//
function repairExistingStyles(
  existing:   { name: string; proxy: Word.Style }[],
  paraMap:    Map<string, ParagraphStyleDef>,
  builtInMap: Map<string, BuiltInStyleDef>,
  runNumber:  number
): void {
  for (const { name, proxy } of existing) {
    // Look up the config def — check custom styles first, then built-ins
    const def = paraMap.get(name) ?? builtInMap.get(name);
    if (!def) continue;  // style exists in doc but not in config — leave it alone

    const fixes: string[] = [];

    // Check and patch font properties (name, size, bold, italic, color)
    fixes.push(...diffAndRepairFont(proxy.font, def.font));

    // Check and patch paragraph properties (spacing, indents, keep rules etc.)
    // def.paragraph may be empty {} for derived styles — safe to call either way
    if (def.paragraph) fixes.push(...diffAndRepairParagraph(proxy.paragraphFormat, def.paragraph));

    // Check and patch quickStyle (whether style appears in the ribbon)
    if (def.quickStyle !== undefined && proxy.quickStyle !== def.quickStyle) {
      fixes.push(`quickStyle: was "${proxy.quickStyle}" → set "${def.quickStyle}"`);
      proxy.quickStyle = def.quickStyle;
    }

    if (fixes.length > 0) {
      console.log(`  [Run ${runNumber}] ✎ Repaired "${name}":\n    ${fixes.join("\n    ")}`);
    }
  }
}


// ═══════════════════════════════════════════════════════════════════════════
//  ensureRunStyles()  — per-run lightweight check
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ensures only the styles needed by a specific run number exist.
 * Uses the runStyleMap from config.
 *
 * Performance contract:
 * - Common case (all styles already exist): exactly ONE context.sync()
 *   round-trip total, regardless of how many styles the run requires.
 * - Missing-style case (rare, only on a fresh document or after manual
 *   deletion): one batched existence sync, then 2 syncs per missing
 *   style — the first to materialize the new style proxy, the second
 *   to commit its properties.
 *
 * Call at the start of any run:
 *   await ensureRunStyles(context, 7);
 *
 * Why this matters: the previous implementation did 1 sync PER STYLE just
 * to check if it existed (so Run 7 with 7 required styles cost 7 round-
 * trips even when nothing needed creating). Multiplied across runs, that
 * was the visible "checking and creating styles takes a long time" delay.
 */
export async function ensureRunStyles(
  context: Word.RequestContext,
  runNumber: number
): Promise<void> {
  const key = String(runNumber);
  const requiredStyles: string[] = config.runStyleMap[key] || [];
  if (requiredStyles.length === 0) return;

  // ── Batched existence + property check: ONE sync for ALL required styles ──
  //
  // We load isNullObject AND every font/paragraph property we care about in
  // the same round-trip. For styles that already exist this lets us diff and
  // repair them without a second sync. For missing styles we fall through to
  // the creation path as before.
  const STYLE_LOAD_PROPS = [
    "isNullObject",
    "font/name", "font/size", "font/bold", "font/italic", "font/color",
    "paragraphFormat/spaceBefore", "paragraphFormat/spaceAfter",
    "paragraphFormat/keepWithNext", "paragraphFormat/keepTogether",
    "paragraphFormat/widowControl", "paragraphFormat/leftIndent",
    "paragraphFormat/firstLineIndent", "paragraphFormat/alignment",
    "quickStyle",
  ].join(",");

  const styleProxies = requiredStyles.map(name => ({
    name,
    proxy: context.document.getStyles().getByNameOrNullObject(name),
  }));
  for (const sp of styleProxies) sp.proxy.load(STYLE_LOAD_PROPS);
  await context.sync();

  const requiredSet = new Set(requiredStyles);
  const paraMap    = new Map(
    config.paragraphStyles.filter(d => requiredSet.has(d.name)).map(d => [d.name, d])
  );
  const builtInMap = new Map(
    config.builtInStyles.filter(d => requiredSet.has(d.name)).map(d => [d.name, d])
  );

  const missing  = styleProxies.filter(sp =>  sp.proxy.isNullObject).map(sp => sp.name);
  const existing = styleProxies.filter(sp => !sp.proxy.isNullObject);

  // ── Repair path: style exists — diff properties and patch mismatches ──
  // To skip repairs entirely, comment out this line ↓
  repairExistingStyles(existing, paraMap, builtInMap, runNumber);
  // Commit repairs (no-op if nothing was patched).
  if (existing.length > 0) await context.sync();

  if (missing.length === 0) return;

  // ── Creation path: style is missing — create it from config ──
  //
  // Built-in styles (Headings etc.) should never be missing from a real Word
  // document, but if they are we apply properties only — never addStyle().
  // Custom paragraph styles are created normally via addStyle().
  //
  // Order comes from runStyleMap[N] which is authored dependency-safe:
  // base styles ("caption", "Footnote Text") always precede their
  // derivatives ("Caption Figure", "Footnote Figure", etc.).
  console.log(`  [Run ${runNumber}] ${missing.length}/${requiredStyles.length} style(s) missing — creating: ${missing.join(", ")}`);

  for (const styleName of missing) {
    // ── Built-in: should always exist; if somehow missing just warn ──
    const builtInDef = builtInMap.get(styleName);
    if (builtInDef) {
      console.warn(`  [Run ${runNumber}] ⚠ Built-in style "${styleName}" not found in document — skipping creation`);
      continue;
    }

    // ── Custom paragraph style: create via addStyle() ──
    const newStyle = context.document.addStyle(styleName, Word.StyleType.paragraph);
    // Sync once so the new style proxy materialises before we set properties.
    await context.sync();

    const def = paraMap.get(styleName);
    if (def) {
      // Full config-based creation
      await trySetBaseStyle(context, newStyle, def.basedOn, def._baseFallback);
      applyFont(newStyle.font, def.font);
      applyParagraph(newStyle.paragraphFormat, def.paragraph);
      if (def.quickStyle !== undefined) newStyle.quickStyle = def.quickStyle;
    } else {
      // Fallback: minimal formatting based on name pattern. This branch
      // exists for styles named in runStyleMap but missing from
      // paragraphStyles — should not happen in normal operation, but
      // protects against config drift.
      newStyle.font.name = "Times New Roman";
      if (styleName.startsWith("Table:") || styleName.includes("Footnote")) {
        newStyle.font.size = 10;
        newStyle.paragraphFormat.spaceAfter = 3;
      } else {
        newStyle.font.size = 12;
        newStyle.paragraphFormat.spaceAfter = 6;
      }
    }
  }

  // Final sync commits all property assignments queued in the creation loop.
  await context.sync();
  console.log(`  [Run ${runNumber}] ✓ ${missing.length} style(s) created`);
}